import { useState, useCallback } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { addTenantOverride, type AddOverrideRequest } from '../../services/tuning';

interface AddOverrideModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  onAdded: () => void;
}

const SIGNAL_TYPES = [
  { value: 'title', label: 'Alert Title' },
  { value: 'alertSource', label: 'Alert Source' },
  { value: 'severity', label: 'Severity' },
  { value: 'category', label: 'Category' },
  { value: 'detectionSource', label: 'Detection Source' },
  { value: 'serviceSource', label: 'Service Source' },
];

const ACTIONS = [
  { value: 'remove', label: 'Remove (suppress)' },
  { value: 'reweight', label: 'Reweight (adjust score)' },
  { value: 'add', label: 'Add (force include)' },
] as const;

export function AddOverrideModal({ isOpen, onClose, tenantId, onAdded }: AddOverrideModalProps) {
  const [playbookId, setPlaybookId] = useState('*');
  const [signalType, setSignalType] = useState('title');
  const [signalValue, setSignalValue] = useState('');
  const [action, setAction] = useState<'add' | 'remove' | 'reweight'>('remove');
  const [weightOverride, setWeightOverride] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setPlaybookId('*');
    setSignalType('title');
    setSignalValue('');
    setAction('remove');
    setWeightOverride('');
    setReason('');
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!signalValue.trim()) {
      setError('Signal value is required');
      return;
    }

    if (action === 'reweight') {
      const w = Number(weightOverride);
      if (isNaN(w) || w < 0 || w > 100) {
        setError('Weight must be between 0 and 100');
        return;
      }
    }

    setSaving(true);
    setError(null);

    const body: AddOverrideRequest = {
      playbookId: playbookId.trim() || '*',
      signalType,
      signalValue: signalValue.trim(),
      action,
      weightOverride: action === 'reweight' ? Number(weightOverride) : null,
      reason: reason.trim() || null,
    };

    const result = await addTenantOverride(tenantId, body);
    setSaving(false);

    if (!result.success) {
      setError(result.error ?? 'Failed to add override');
      return;
    }

    reset();
    onAdded();
    onClose();
  }, [tenantId, playbookId, signalType, signalValue, action, weightOverride, reason, reset, onAdded, onClose]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Signal Override"
      description="Create a tuning override for this tenant's signal processing."
      size="lg"
      footer={
        <div className="flex items-center justify-between">
          {error && <p className="text-sm text-red-500">{error}</p>}
          <div className="flex gap-3 ml-auto">
            <Button variant="ghost" onClick={handleClose} disabled={saving}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving...' : 'Add Override'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">Playbook ID</label>
          <input
            type="text"
            value={playbookId}
            onChange={(e) => setPlaybookId(e.target.value)}
            placeholder="* (all playbooks)"
            className="w-full px-3 py-2 text-sm bg-bg-secondary border border-border-default rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent-blue/50"
          />
          <p className="mt-1 text-xs text-text-secondary">Use * to apply to all playbooks</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Signal Type</label>
            <select
              value={signalType}
              onChange={(e) => setSignalType(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-bg-secondary border border-border-default rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-blue/50"
            >
              {SIGNAL_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Action</label>
            <select
              value={action}
              onChange={(e) => setAction(e.target.value as 'add' | 'remove' | 'reweight')}
              className="w-full px-3 py-2 text-sm bg-bg-secondary border border-border-default rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-blue/50"
            >
              {ACTIONS.map((a) => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">Signal Value</label>
          <input
            type="text"
            value={signalValue}
            onChange={(e) => setSignalValue(e.target.value)}
            placeholder={signalType === 'title' ? 'e.g. NRT MFA Rejected by User' : 'Enter value...'}
            className="w-full px-3 py-2 text-sm bg-bg-secondary border border-border-default rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent-blue/50"
          />
        </div>

        {action === 'reweight' && (
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Weight Override (0-100)</label>
            <input
              type="number"
              min={0}
              max={100}
              value={weightOverride}
              onChange={(e) => setWeightOverride(e.target.value)}
              placeholder="e.g. 10"
              className="w-full px-3 py-2 text-sm bg-bg-secondary border border-border-default rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent-blue/50"
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">Reason (optional)</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Why are you adding this override?"
            className="w-full px-3 py-2 text-sm bg-bg-secondary border border-border-default rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent-blue/50 resize-none"
          />
        </div>
      </div>
    </Modal>
  );
}
